import React, { useState, useEffect, useCallback } from 'react';
import config from '../Config';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  formatEasternDate,
  fromDateTimeLocalInEastern,
  toDateTimeLocalInEastern,
} from '../Utils/dateTime';

// Separate component for service checkbox to prevent re-render issues
const ServiceCheckbox = React.memo(({ service, isChecked, onToggle }) => {
  const handleChange = (e) => {
    e.stopPropagation();
    onToggle(service.service_id);
  };

  return (
    <div className="flex items-center space-x-2 mb-1">
      <input
        type="checkbox"
        id={`service-checkbox-${service.service_id}`}
        checked={isChecked}
        onChange={handleChange}
        className="rounded"
        value={service.service_id}
      />
      <label 
        htmlFor={`service-checkbox-${service.service_id}`}
        className="text-sm cursor-pointer select-none"
        onClick={(e) => e.preventDefault()}
      >
        {service.title}
      </label>
    </div>
  );
});

function Promo() {
  const [promos, setPromos] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    code: '',
    discount_percent: '',
    service_ids: [],
    description: '',
    max_discount: '',
    min_order_amount: '',
    valid_until: '',
    usage_limit: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchPromos = useCallback(async (options = {}) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${config.apiUrl}/api/v1/admin/promo`;
      
      const params = new URLSearchParams();
      const search = options.search ?? searchTerm;
      const active = options.active ?? activeFilter;
      if (search) params.append('search', search);
      if (active !== 'all') params.append('active', active === 'active');
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Handle the nested response structure
        let promoData = [];
        if (result.success && result.data && result.data.promo_codes) {
          promoData = result.data.promo_codes;
        } else if (Array.isArray(result)) {
          promoData = result;
        } else if (Array.isArray(result.data)) {
          promoData = result.data;
        }
        
        setPromos(Array.isArray(promoData) ? promoData : []);
      } else {
        throw new Error('Failed to fetch promo codes');
      }
    } catch (error) {
      toast.error(error.message);
      setError(error.message);
      setPromos([]); // Ensure promos is always an array
    } finally {
      setLoading(false);
    }
  }, [searchTerm, activeFilter]);

  const fetchServices = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/service?disable_pagination=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      } else {
        throw new Error('Failed to fetch services');
      }
    } catch (error) {
      toast.error(error.message);
    }
  }, []);

  useEffect(() => {
    fetchPromos();
    fetchServices();
  }, [fetchPromos, fetchServices]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      const url = isEditing 
        ? `${config.apiUrl}/api/v1/admin/promo/${editingId}`
        : `${config.apiUrl}/api/v1/admin/promo`;
        
      const method = isEditing ? 'PUT' : 'POST';

      // Filter out null, undefined, and empty string values from service_ids
      const cleanServiceIds = (formData.service_ids || []).filter(id => id != null && id !== '' && id !== undefined);

      // Prepare form data
      const submitData = {
        ...formData,
        discount_percent: parseFloat(formData.discount_percent),
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
        min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : null,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        valid_until: formData.valid_until ? fromDateTimeLocalInEastern(formData.valid_until) : null,
        service_ids: cleanServiceIds
      };

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast.success(isEditing ? 'Promo code updated successfully!' : 'Promo code created successfully!');
        setShowModal(false);
        resetForm();
        fetchPromos();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to save promo code');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (promo) => {
    // Filter out null/undefined values from service_ids when editing
    const cleanServiceIds = (promo.service_ids || []).filter(id => id != null && id !== '' && id !== undefined);
    
    setFormData({
      code: promo.code,
      discount_percent: promo.discount_percent,
      service_ids: cleanServiceIds,
      description: promo.description || '',
      max_discount: promo.max_discount || '',
      min_order_amount: promo.min_order_amount || '',
      valid_until: promo.valid_until ? toDateTimeLocalInEastern(promo.valid_until) : '',
      usage_limit: promo.usage_limit || ''
    });
    setIsEditing(true);
    setEditingId(promo.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this promo code?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${config.apiUrl}/api/v1/admin/promo/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          toast.success('Promo code deleted successfully!');
          fetchPromos();
        } else {
          toast.error('Failed to delete promo code');
        }
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/promo/${id}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Promo code status updated successfully!');
        fetchPromos();
      } else {
        toast.error('Failed to update promo code status');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_percent: '',
      service_ids: [],
      description: '',
      max_discount: '',
      min_order_amount: '',
      valid_until: '',
      usage_limit: ''
    });
    setIsEditing(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceChange = useCallback((serviceId) => {
    // Validate serviceId is not null or undefined
    if (!serviceId || serviceId === null || serviceId === undefined) {
      console.warn('Invalid service ID:', serviceId);
      return;
    }

    setFormData(prev => {
      const currentIds = Array.isArray(prev.service_ids) ? prev.service_ids : [];
      const isSelected = currentIds.includes(serviceId);
      
      let newIds;
      if (isSelected) {
        // Remove the service ID
        newIds = currentIds.filter(id => id !== serviceId);
      } else {
        // Add the service ID (only if it's valid)
        newIds = [...currentIds, serviceId];
      }
      
      // Filter out any null/undefined values that might have slipped in
      newIds = newIds.filter(id => id != null && id !== '' && id !== undefined);
      
      return {
        ...prev,
        service_ids: newIds
      };
    });
  }, []);

  const handleSearch = () => {
    fetchPromos({ search: searchTerm, active: activeFilter });
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setTimeout(() => fetchPromos({ search: searchTerm, active: filter }), 100);
  };

  const showDetails = (promo) => {
    setSelectedPromo(promo);
    setShowDetailsModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No expiry';
    return formatEasternDate(dateString);
  };

  const getServiceNames = (serviceIds) => {
    if (!serviceIds || !Array.isArray(serviceIds) || serviceIds.length === 0) {
      return 'All services';
    }
    
    const serviceNames = serviceIds
      .map(id => {
        const service = services.find(s => s.service_id === id);
        return service ? service.title : `Unknown (${id})`;
      })
      .filter(name => name !== null);
    
    return serviceNames.length > 0 ? serviceNames.join(', ') : 'All services';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Topbar />
        <div className="flex flex-1 items-center justify-center px-4 py-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Topbar />
        <div className="flex flex-1 items-center justify-center px-4 py-12">
          <div className="text-red-500 text-lg sm:text-xl text-center">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Topbar />
      <div className="container mx-auto max-w-7xl px-4 py-6 sm:py-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 text-left">Promo Codes</h1>
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-full sm:w-auto shrink-0"
          >
            Add New Promo Code
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by code or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <button
              onClick={handleSearch}
              className="bg-gray-500 hover:bg-gray-700 text-white px-4 py-2 rounded"
            >
              Search
            </button>
          </div>
          
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => handleFilterChange('all')}
              className={`px-4 py-2 rounded ${activeFilter === 'all' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              All
            </button>
            <button
              onClick={() => handleFilterChange('active')}
              className={`px-4 py-2 rounded ${activeFilter === 'active' 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Active
            </button>
            <button
              onClick={() => handleFilterChange('inactive')}
              className={`px-4 py-2 rounded ${activeFilter === 'inactive' 
                ? 'bg-red-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Inactive
            </button>
          </div>
        </div>

        {/* Promo Codes Table */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Services
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valid Until
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(promos || []).map((promo) => (
                <tr key={promo.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{promo.code}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{promo.discount_percent}%</div>
                    {promo.max_discount && (
                      <div className="text-xs text-gray-500">Max: {formatCurrency(promo.max_discount)}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 truncate max-w-xs">
                      {promo.description || 'No description'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 truncate max-w-xs">
                      {getServiceNames(promo.service_ids)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(promo.valid_until)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      promo.active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {promo.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => showDetails(promo)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleEdit(promo)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleStatus(promo.id)}
                        className={`${promo.active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                      >
                        {promo.active ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => handleDelete(promo.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {(!promos || promos.length === 0) && (
            <div className="text-center py-8">
              <div className="text-gray-500">No promo codes found</div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-[120]">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {isEditing ? 'Edit Promo Code' : 'Add New Promo Code'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Code*</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                    placeholder="e.g., SAVE10"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Discount Percent*</label>
                  <input
                    type="number"
                    name="discount_percent"
                    value={formData.discount_percent}
                    onChange={handleInputChange}
                    required
                    min="0"
                    max="100"
                    step="0.01"
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                    placeholder="10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Description of the promo code..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Max Discount (CAD)</label>
                  <input
                    type="number"
                    name="max_discount"
                    value={formData.max_discount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                    placeholder="5000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Min Order Amount (CAD)</label>
                  <input
                    type="number"
                    name="min_order_amount"
                    value={formData.min_order_amount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                    placeholder="2000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Valid Until (Eastern time)</label>
                  <input
                    type="datetime-local"
                    name="valid_until"
                    value={formData.valid_until}
                    onChange={handleInputChange}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Usage Limit</label>
                  <input
                    type="number"
                    name="usage_limit"
                    value={formData.usage_limit}
                    onChange={handleInputChange}
                    min="1"
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                    placeholder="100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Services to Promo
                </label>
                <div className="text-xs text-gray-500 mb-2">
                  Promo will only apply when ALL selected services are in the bundle
                </div>
                
                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                  {services.map((service, index) => (
                    <ServiceCheckbox 
                      key={`service-${service.service_id}-${index}`}
                      service={service}
                      isChecked={formData.service_ids.includes(service.service_id)}
                      onToggle={handleServiceChange}
                    />
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-black font-bold py-2 px-4 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  {isEditing ? 'Update' : 'Create'} Promo Code
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedPromo && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-[120]">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Promo Code Details</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Code</label>
                  <div className="mt-1 text-lg font-semibold">{selectedPromo.code}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Discount</label>
                  <div className="mt-1 text-lg">{selectedPromo.discount_percent}%</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <div className="mt-1">{selectedPromo.description || 'No description'}</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Max Discount</label>
                  <div className="mt-1">
                    {selectedPromo.max_discount ? formatCurrency(selectedPromo.max_discount) : 'No limit'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Min Order Amount</label>
                  <div className="mt-1">
                    {selectedPromo.min_order_amount ? formatCurrency(selectedPromo.min_order_amount) : 'No minimum'}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Valid Until</label>
                  <div className="mt-1">{formatDate(selectedPromo.valid_until)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Usage Limit</label>
                  <div className="mt-1">{selectedPromo.usage_limit || 'Unlimited'}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <span className={`mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  selectedPromo.active 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {selectedPromo.active ? 'Active' : 'Inactive'}
                </span>
              </div>

               {selectedPromo.service_ids && selectedPromo.service_ids.length > 0 && (
                 <div>
                   <label className="block text-sm font-medium text-gray-700">Required Services</label>
                   <div className="text-xs text-gray-500 mb-1">
                     All these services must be in the bundle for promo to apply
                   </div>
                   <div className="mt-1">
                     {selectedPromo.service_ids.map(serviceId => {
                       const service = services.find(s => s.service_id === serviceId);
                       return service ? (
                         <span key={serviceId} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-2 mb-1">
                           {service.title}
                         </span>
                       ) : null;
                     })}
                   </div>
                 </div>
               )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="bg-gray-300 hover:bg-gray-400 text-black font-bold py-2 px-4 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default Promo;
